import React, { type KeyboardEvent, type ReactNode, useId, useState } from 'react';
import styled from 'styled-components';

import { type CardDispute, type CardDisputeEvidence } from '@column/api';
import { formatNumber } from '@column/column-ui-kit';
import { Button } from '@column/ui';

import {
  CARD_DISPUTE_EVIDENCE_TYPE_LABELS,
  getDisputeResolutionLabel,
  getSubmissionStatusLabel,
} from '~/lib/disputes/labels';
import { formatDateTime, formatString } from '~/util';

import {
  getLifecycleActorSummary,
  getLifecycleResponseActions,
  getStageEvents,
  getStageState,
  getUnlinkedEvidence,
  lifecycleStageLabel,
} from './lifecycle';
import {
  DISPUTE_TOPOLOGIES,
  type DisputeStage,
  LIFECYCLE_STAGE_ORDER,
  getLifecycleEdges,
  isLifecycleWorkflow,
} from './topology';

export interface DisputeLifecycleGraphProps {
  dispute: CardDispute;
  onSelectStage?: (stage: DisputeStage) => void;
  canManage?: boolean;
  actionsHref?: string;
  /** Part 08 can supply tracking backed by confirmed filing/ruling metadata. */
  arbitrationDetails?: ReactNode;
}

const Root = styled.details`
  min-width: 0;
  padding: 20px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  background: var(--color-card);
  color: var(--color-foreground);
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: anywhere;

  > summary {
    cursor: pointer;
    font-size: 18px;
    font-weight: 600;
  }
  &[open] > summary {
    margin-bottom: 12px;
  }
  h3,
  h4,
  p {
    margin: 0;
  }
  h3 {
    font-size: 18px;
    font-weight: 600;
  }
  h4 {
    font-size: 14px;
    font-weight: 600;
  }
  a {
    color: var(--color-primary);
    text-decoration: underline;
  }
  button:focus-visible,
  a:focus-visible,
  summary:focus-visible,
  [tabindex]:focus-visible {
    outline: 3px solid var(--color-primary);
    outline-offset: 3px;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const Legend = styled(Stack)`
  padding: 12px 16px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);

  ul {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 24px;
    margin: 0;
    padding: 4px;
    list-style: none;
  }
  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
  }
  p {
    font-size: 12px;
    color: var(--color-muted-foreground);
  }
`;

const LegendStage = styled.span`
  display: inline-block;
  flex: 0 0 28px;
  height: 18px;
  border: 1px solid var(--color-muted-foreground);
  border-radius: 4px;
  background: var(--color-card);

  &[data-style='current'] {
    border: 3px solid var(--color-primary);
  }
  &[data-style='selected'] {
    box-shadow:
      0 0 0 2px var(--color-card),
      0 0 0 4px var(--color-primary);
  }
  &[data-style='unrecorded'] {
    border-style: dashed;
  }
`;

const Columns = styled.div`
  display: grid;
  gap: 20px;
  margin-top: 20px;
  align-items: start;
  @media (min-width: 1280px) {
    grid-template-columns: minmax(0, 1fr) 300px;
  }
`;

const Scroll = styled.div`
  overflow-x: auto;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
`;

const Canvas = styled.div`
  position: relative;
  width: 1072px;
  svg {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
`;

const StageButton = styled.button`
  position: absolute;
  width: 224px;
  height: 112px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  border: 1px dashed var(--color-muted-foreground);
  border-radius: 8px;
  background: var(--color-card);
  color: var(--color-muted-foreground);
  text-align: left;
  font: inherit;
  font-size: 13px;
  cursor: pointer;

  &[data-state='Recorded stage'] {
    border-style: solid;
    color: var(--color-foreground);
  }
  &[aria-current='step'] {
    border: 3px solid var(--color-primary);
    color: var(--color-foreground);
  }
  &[aria-pressed='true'] {
    box-shadow:
      0 0 0 2px var(--color-card),
      0 0 0 4px var(--color-primary);
  }
  &:hover {
    background: var(--color-muted);
  }
  strong {
    font-weight: 600;
  }
  span {
    font-size: 12px;
  }
`;

const EdgeLabel = styled.div`
  position: absolute;
  width: 212px;
  font-size: 11px;
  line-height: 1.4;
  color: var(--color-muted-foreground);
  pointer-events: none;
  &[data-highlighted='true'] {
    color: var(--color-primary);
  }
  strong {
    display: block;
    font-weight: 600;
  }
`;

const Panel = styled(Stack)`
  padding: 16px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  @media (min-width: 1280px) {
    position: sticky;
    top: 120px;
    max-height: calc(100vh - 220px);
    overflow-y: auto;
  }
`;

const Records = styled.ol`
  list-style: decimal;
  padding-left: 20px;
  margin: 0;
  li + li {
    margin-top: 16px;
  }
  p {
    white-space: pre-wrap;
  }
`;

const HistoryStages = styled.ul`
  list-style: decimal;
  margin: 12px 0 0;
  padding-left: 24px;
  button {
    max-width: 100%;
    height: auto;
    min-height: 32px;
    padding-block: 6px;
    white-space: normal;
    text-align: left;
  }
  > li {
    margin-bottom: 20px;
  }
  ul {
    list-style: disc;
    padding-left: 20px;
  }
`;

const RecordedTime = ({ value, label = 'Recorded in Column' }: { value?: string | null; label?: string }) => (
  <p>
    {label}:{' '}
    {value && Number.isFinite(Date.parse(value)) ? (
      <time dateTime={value}>{formatDateTime(value, { timeZoneFormat: 'utc' })}</time>
    ) : (
      'Time unavailable'
    )}
  </p>
);

const EvidenceRecords = ({ entries }: { entries: CardDisputeEvidence[] }) => (
  <Records>
    {entries.map((evidence) => (
      <li key={evidence.id} data-evidence-id={evidence.id}>
        <strong>
          {evidence.evidence_type
            ? (CARD_DISPUTE_EVIDENCE_TYPE_LABELS[evidence.evidence_type] ?? formatString(evidence.evidence_type))
            : 'Evidence'}
        </strong>
        <p>{evidence.id}</p>
        {evidence.document_id && <p>Document: {evidence.document_id}</p>}
        {evidence.narrative && <p>{evidence.narrative}</p>}
        <p>
          {evidence.direction === 'inbound'
            ? 'Received from network'
            : evidence.submitted_at
              ? 'Submitted to the Card Network'
              : 'Attached to dispute; not yet submitted to the Card Network'}
        </p>
        <RecordedTime value={evidence.created_at} />
        {evidence.submitted_at && <RecordedTime value={evidence.submitted_at} label="Network delivery" />}
      </li>
    ))}
  </Records>
);

const StageHistory = ({ dispute, stage, panelId }: { dispute: CardDispute; stage: DisputeStage; panelId: string }) => {
  const events = getStageEvents(dispute, stage);
  const unlinkedEvidence = getUnlinkedEvidence(dispute);
  return (
    <Panel role="region" aria-label="Selected stage details" id={panelId} tabIndex={-1}>
      <h3>{lifecycleStageLabel(stage)}</h3>
      <p>
        {getStageState(dispute, stage)} · {events.length} recorded {events.length === 1 ? 'event' : 'events'}
      </p>
      {events.length ? (
        <Records>
          {events.map((event) => {
            const evidence = (dispute.evidence ?? []).filter((entry) => entry.card_dispute_event_id === event.id);
            return (
              <li key={event.id} data-event-id={event.id}>
                <strong>
                  {event.response_action
                    ? `Response recorded — ${formatString(event.response_action)}`
                    : event.evidence_document_id || event.evidence_narrative
                      ? 'Evidence recorded'
                      : 'Stage recorded'}
                </strong>
                <p>{event.id}</p>
                <RecordedTime value={event.created_at} />
                {event.response_amount != null && (
                  <p>Accepted liability: {formatNumber(event.response_amount, dispute.currency)}</p>
                )}
                {event.evidence_narrative && <p>{event.evidence_narrative}</p>}
                {event.evidence_document_id && <p>Document: {event.evidence_document_id}</p>}
                {event.linked_transaction_event_id && <p>Transaction event: {event.linked_transaction_event_id}</p>}
                {evidence.length > 0 && (
                  <>
                    <h4>Related evidence</h4>
                    <EvidenceRecords entries={evidence} />
                  </>
                )}
              </li>
            );
          })}
        </Records>
      ) : (
        <p>No recorded events for this stage. Its place in the diagram does not establish that it occurred.</p>
      )}
      {unlinkedEvidence.length > 0 && (
        <details>
          <summary>Unlinked evidence ({unlinkedEvidence.length})</summary>
          <p>This evidence has no matching event in the available history.</p>
          <EvidenceRecords entries={unlinkedEvidence} />
        </details>
      )}
    </Panel>
  );
};

export const DisputeLifecycleGraph = (props: DisputeLifecycleGraphProps) => (
  <LifecycleContent key={`${props.dispute.id}:${props.dispute.workflow}:${props.dispute.stage}`} {...props} />
);

const LifecycleContent = ({
  dispute,
  onSelectStage,
  canManage = false,
  actionsHref,
  arbitrationDetails,
}: DisputeLifecycleGraphProps) => {
  const [selectedStage, setSelectedStage] = useState(dispute.stage);
  const instanceId = useId();
  const panelId = `${instanceId}-details`;
  const knownWorkflow = isLifecycleWorkflow(dispute.workflow);
  const stages = knownWorkflow ? LIFECYCLE_STAGE_ORDER[dispute.workflow] : [];
  const hasGraph = stages.includes(dispute.stage);
  const edges = knownWorkflow ? getLifecycleEdges(DISPUTE_TOPOLOGIES[dispute.workflow]) : [];
  const responseActions = getLifecycleResponseActions(dispute);
  const mainStages = stages.filter(
    (stage) => !['dispute_won', 'dispute_close', 'merchant_response_recalled'].includes(stage)
  );
  const endingY = 16 + mainStages.length * 152;
  const positions = Object.fromEntries(
    stages.map((stage) => [
      stage,
      stage === 'dispute_won'
        ? { x: 24, y: endingY }
        : stage === 'dispute_close'
          ? { x: 824, y: endingY }
          : stage === 'merchant_response_recalled'
            ? { x: 80, y: 320 }
            : { x: 480, y: 16 + mainStages.indexOf(stage) * 152 },
    ])
  );
  const renderedEdges = edges.map((edge) => {
    const from = positions[edge.from];
    const to = positions[edge.to];
    const row = mainStages.indexOf(edge.from);
    const isMain = to.x === 480;
    const isRecall = edge.to === 'merchant_response_recalled';
    const lane = to.x === 24 ? 40 + row * 16 : 1032 - row * 16;
    return {
      ...edge,
      highlighted: edge.from === selectedStage,
      path: isMain
        ? `M ${from.x + 112} ${from.y + 112} V ${to.y - 4}`
        : isRecall
          ? `M ${from.x} ${from.y + 56} H ${to.x + 228}`
          : `M ${from.x + (to.x === 24 ? 0 : 224)} ${from.y + 100} H ${lane} V ${to.y - 4}`,
      labelX: isMain ? 612 : isRecall ? 312 : to.x === 24 ? 248 : 720,
      labelY: isMain ? from.y + 119 : isRecall ? from.y + 50 : from.y + 94,
      labelWidth: isRecall ? 156 : 212,
      showDescription: !isMain,
    };
  });
  const select = (stage: DisputeStage) => {
    setSelectedStage(stage);
    onSelectStage?.(stage);
  };
  const navigateNodes = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (index + 1) % stages.length;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft')
      nextIndex = (index + stages.length - 1) % stages.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = stages.length - 1;
    else return;
    event.preventDefault();
    document.getElementById(`${instanceId}-${stages[nextIndex]}`)?.focus();
  };

  return (
    <Root aria-label="Dispute lifecycle" data-testid="dispute-lifecycle" data-dd-privacy="hidden">
      <summary>{knownWorkflow ? `${formatString(dispute.workflow)} lifecycle` : 'Dispute lifecycle'}</summary>
      <Stack>
        <p>
          <strong>Current stage: {lifecycleStageLabel(dispute.stage)}</strong>
        </p>
        <p>
          Delivery:{' '}
          {dispute.submission_status ? getSubmissionStatusLabel(dispute.submission_status) : 'Status unavailable'}.
          Delivery does not advance the stage.
        </p>
        <p>{getLifecycleActorSummary(dispute)}</p>
        {(dispute.stage === 'dispute_won' || dispute.stage === 'dispute_close') && (
          <p>{getDisputeResolutionLabel(dispute.resolution_reason, dispute.stage)}</p>
        )}
        {dispute.stage === 'arbitration' && arbitrationDetails}
        {responseActions.length > 0 && (
          <div>
            <h4>Available issuer responses</h4>
            <p>{responseActions.map(({ label }) => label).join(' · ')}</p>
            {canManage && actionsHref ? (
              <a
                href={actionsHref}
                onClick={(event) => {
                  if (actionsHref.startsWith('#')) {
                    // Dashboard previews set a base URL, so native fragment links would leave this detail route.
                    event.preventDefault();
                    const target = document.getElementById(actionsHref.slice(1));
                    target?.scrollIntoView({ block: 'center' });
                    target?.focus({ preventScroll: true });
                  }
                }}
              >
                View actions
              </a>
            ) : (
              <p>
                {canManage
                  ? 'Review these responses in the Actions area when available.'
                  : 'Only platform owners and admins can respond.'}
              </p>
            )}
          </div>
        )}
        {!dispute.events?.length && <p>Stage history is unavailable. Only the current server state is known.</p>}
        {!hasGraph && (
          <p>
            Diagram unavailable for workflow “{dispute.workflow || 'Unavailable'}” and stage “
            {dispute.stage || 'Unavailable'}”. The current record and available history are shown below.
          </p>
        )}
        {hasGraph && (
          <>
            <Legend role="note" aria-label="Diagram legend">
              <h4>Diagram legend</h4>
              <ul>
                <li>
                  <LegendStage aria-hidden="true" data-style="current" />
                  Current stage
                </li>
                <li>
                  <LegendStage aria-hidden="true" />
                  Recorded stage
                </li>
                <li>
                  <LegendStage aria-hidden="true" data-style="selected" />
                  Selected stage
                </li>
                <li>
                  <LegendStage aria-hidden="true" data-style="unrecorded" />
                  Unrecorded stage
                </li>
                <li>
                  <svg width="32" height="18" viewBox="0 0 32 18" aria-hidden="true" focusable="false">
                    <path
                      d="M 0 9 H 26"
                      stroke="var(--color-muted-foreground)"
                      strokeWidth="1.5"
                      strokeDasharray="5 5"
                    />
                    <path d="M 25 5 L 31 9 L 25 13 Z" fill="var(--color-muted-foreground)" />
                  </svg>
                  Possible transition
                </li>
                <li>
                  <svg width="32" height="18" viewBox="0 0 32 18" aria-hidden="true" focusable="false">
                    <path d="M 0 9 H 26" stroke="var(--color-primary)" strokeWidth="2.5" strokeDasharray="5 5" />
                    <path d="M 25 5 L 31 9 L 25 13 Z" fill="var(--color-primary)" />
                  </svg>
                  From selected stage
                </li>
              </ul>
              <p>
                Select a stage to highlight its possible next steps. Recorded history shows what has happened; available
                responses depend on the current dispute.
              </p>
            </Legend>
          </>
        )}
      </Stack>
      <Columns>
        {hasGraph ? (
          <Stack>
            <Scroll role="region" aria-label="Lifecycle graph, horizontally scrollable" tabIndex={0}>
              <Canvas style={{ height: endingY + 144 }}>
                <svg width="1072" height={endingY + 144} aria-hidden="true" focusable="false">
                  <defs>
                    <marker
                      id={`${instanceId}-arrow`}
                      viewBox="0 0 10 10"
                      refX="9"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--color-muted-foreground)" />
                    </marker>
                    <marker
                      id={`${instanceId}-selected-arrow`}
                      viewBox="0 0 10 10"
                      refX="9"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--color-primary)" />
                    </marker>
                  </defs>
                  {[...renderedEdges]
                    .sort((a, b) => Number(a.highlighted) - Number(b.highlighted))
                    .map((edge) => (
                      <path
                        key={`${edge.from}-${edge.to}`}
                        data-edge={`${edge.from}:${edge.to}`}
                        data-highlighted={edge.highlighted}
                        d={edge.path}
                        fill="none"
                        stroke={edge.highlighted ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                        strokeWidth={edge.highlighted ? 2.5 : 1.5}
                        strokeDasharray="5 5"
                        markerEnd={`url(#${instanceId}-${edge.highlighted ? 'selected-arrow' : 'arrow'})`}
                      />
                    ))}
                </svg>
                {renderedEdges.map((edge) => (
                  <EdgeLabel
                    key={`${edge.from}-${edge.to}`}
                    data-edge-label={`${edge.from}:${edge.to}`}
                    data-highlighted={edge.highlighted}
                    aria-hidden="true"
                    style={{
                      left: edge.labelX,
                      top: edge.labelY,
                      width: edge.labelWidth,
                      transform: edge.showDescription ? 'translateY(-100%)' : undefined,
                    }}
                  >
                    <strong>{edge.actor}</strong>
                    {edge.showDescription && edge.description}
                  </EdgeLabel>
                ))}
                {stages.map((stage, index) => {
                  const state = getStageState(dispute, stage);
                  const eventCount = getStageEvents(dispute, stage).length;
                  const transitions = edges.filter((edge) => edge.from === stage);
                  const transitionDescription = transitions.length
                    ? `Possible next stages: ${transitions.map((edge) => `${lifecycleStageLabel(edge.to)} — ${edge.actor}: ${edge.description}`).join('. ')}.`
                    : stage === 'merchant_response_recalled'
                      ? 'No ordinary outgoing route is defined. Follow the server record.'
                      : 'Terminal stage; no further transitions.';
                  return (
                    <React.Fragment key={stage}>
                      <StageButton
                        type="button"
                        id={`${instanceId}-${stage}`}
                        style={{ left: positions[stage].x, top: positions[stage].y }}
                        data-stage={stage}
                        data-state={state}
                        aria-label={`${lifecycleStageLabel(stage)}. ${state}. ${eventCount} recorded events. Inspect history.`}
                        aria-current={stage === dispute.stage ? 'step' : undefined}
                        aria-pressed={stage === selectedStage}
                        aria-controls={panelId}
                        aria-describedby={`${instanceId}-${stage}-transitions`}
                        onClick={() => select(stage)}
                        onKeyDown={(event) => navigateNodes(event, index)}
                      >
                        <strong>{lifecycleStageLabel(stage)}</strong>
                        <span>{state}</span>
                        {eventCount > 0 && (
                          <span>
                            {eventCount} recorded {eventCount === 1 ? 'event' : 'events'}
                          </span>
                        )}
                      </StageButton>
                      <span className="sr-only" id={`${instanceId}-${stage}-transitions`}>
                        {transitionDescription}
                      </span>
                    </React.Fragment>
                  );
                })}
              </Canvas>
            </Scroll>
          </Stack>
        ) : (
          <HistoryStages aria-label="Available stage history">
            {[...new Set([dispute.stage, ...(dispute.events ?? []).map((event) => event.stage)])].map((stage) => (
              <li key={stage ?? 'unknown'}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  aria-pressed={stage === selectedStage}
                  aria-current={stage === dispute.stage ? 'step' : undefined}
                  onClick={() => select(stage)}
                >
                  {lifecycleStageLabel(stage)}
                </Button>
              </li>
            ))}
          </HistoryStages>
        )}
        <StageHistory dispute={dispute} stage={selectedStage} panelId={panelId} />
      </Columns>
    </Root>
  );
};
